import { useLayoutEffect, useRef } from 'react';

const getStorageKey = (key: string) => `scrollPosition_${key}`;

export const useScrollRestore = (key: string) => {
    const scrollRef = useRef<HTMLElement | null>(null);

    // Ref callback to restore scroll immediately when element is mounted
    const setScrollRef = (element: HTMLElement | null) => {
        scrollRef.current = element;
        if (element) {
            // Restore scroll synchronously to avoid jump
            try {
                const storageKey = getStorageKey(key);
                const stored = sessionStorage.getItem(storageKey);
                if (stored) {
                    const scrollPosition = parseInt(stored, 10);
                    if (!isNaN(scrollPosition)) {
                        // Use requestAnimationFrame to ensure DOM is ready
                        requestAnimationFrame(() => {
                            if (element) {
                                element.scrollTop = scrollPosition;
                            }
                        });
                        return;
                    }
                }
            } catch (e) {
                // Ignore storage errors
            }
            // If no stored position, ensure we start at top
            requestAnimationFrame(() => {
                if (element) {
                    element.scrollTop = 0;
                }
            });
        }
    };

    // Restore scroll when key changes
    useLayoutEffect(() => {
        if (!scrollRef.current) return;

        try {
            const storageKey = getStorageKey(key);
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                const scrollPosition = parseInt(stored, 10);
                if (!isNaN(scrollPosition)) {
                    scrollRef.current.scrollTop = scrollPosition;
                    return;
                }
            }
        } catch (e) {
            // Ignore storage errors
        }
        // If no stored position, reset to top
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [key]);

    const handleScroll = () => {
        if (scrollRef.current) {
            try {
                const storageKey = getStorageKey(key);
                sessionStorage.setItem(storageKey, scrollRef.current.scrollTop.toString());
            } catch (e) {
                // Ignore storage errors
            }
        }
    };

    return { setScrollRef, handleScroll };
};
